"use client"

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Link from 'next/link'
import { useAuth } from '@/components/providers/supabase-auth-provider'
import { FaGoogle } from 'react-icons/fa'
import { Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const { signIn, signInWithGoogle, error } = useAuth()

  // Handle email/password sign in
  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !password) {
      setErrorMessage('Please enter both email and password')
      return
    }
    
    try {
      setIsLoading(true)
      setErrorMessage(null)
      
      await signIn(email, password)
      // Redirect handled in auth provider
    } catch (error: any) {
      console.error('Login error:', error)
      setErrorMessage(error.message || 'Failed to sign in')
    } finally {
      setIsLoading(false)
    }
  }
  
  // Handle Google sign in
  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true)
      setErrorMessage(null)
      
      await signInWithGoogle()
      // Auth provider and callback handler will manage redirection
    } catch (error: any) {
      console.error('Google sign-in error:', error)
      setErrorMessage(error.message || 'Failed to sign in with Google')
      setIsLoading(false)
    }
  }
  
  return (
    <div className="flex min-h-screen flex-1">
      <motion.div 
        className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24"
        initial="hidden"
        animate="visible"
        variants={staggerChildren}
      >
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <motion.div variants={fadeIn}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              className="mb-8 flex items-center"
            >
              <div className="p-2 bg-gradient-to-br from-primary/80 to-primary/30 rounded-lg shadow-lg">
                <h1 className="text-3xl font-bold text-white">
                  <span className="opacity-90">Gov</span>
                  <span className="opacity-100">Con</span>
                  <motion.span 
                    initial={{ opacity: 0.7 }}
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="ml-1 text-white"
                  >
                    Data
                  </motion.span>
                </h1>
              </div>
            </motion.div>
            <h2 className="mt-2 text-2xl font-bold leading-9 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
              Welcome back
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Not a member?{' '}
              <Link href="/signup" className="font-semibold text-primary hover:text-primary/80 transition-colors">
                Sign up now
              </Link>
            </p>
          </motion.div>

          <motion.div className="mt-10" variants={fadeIn}>
            <div>
              <form onSubmit={handleEmailSignIn} className="space-y-6">
                <AnimatePresence>
                  {errorMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="rounded-md bg-destructive/10 p-4 text-sm text-destructive"
                    >
                      {errorMessage}
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <motion.div variants={fadeIn}>
                  <label htmlFor="email" className="block text-sm font-medium leading-6">
                    Email address
                  </label>
                  <div className="mt-2">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11 transition-all duration-200 hover:border-primary focus:border-primary"
                    />
                  </div>
                </motion.div>

                <motion.div variants={fadeIn}>
                  <label htmlFor="password" className="block text-sm font-medium leading-6">
                    Password
                  </label>
                  <div className="mt-2 relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 transition-all duration-200 hover:border-primary focus:border-primary"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="flex items-center justify-end mt-1">
                    <Link href="/forgot-password" className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                      Forgot password?
                    </Link>
                  </div>
                </motion.div>

                <motion.div variants={fadeIn}>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="relative flex w-full justify-center h-11 transition-all duration-200"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <span className="flex items-center">
                        Sign in
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </span>
                    )}
                    
                    {/* Hover effect overlay */}
                    <span className="absolute inset-0 bg-gradient-to-tr from-primary/0 to-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </Button>
                </motion.div>
              </form>
            </div>

            <motion.div className="mt-6" variants={fadeIn}>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-sm font-medium leading-6">
                  <span className="bg-background px-6 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <motion.div 
                className="mt-6"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  variant="outline"
                  className="relative flex w-full items-center justify-center h-11 hover:border-primary transition-all duration-200 group"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Image 
                        src="/icons/google.svg" 
                        alt="Google logo" 
                        width={20} 
                        height={20} 
                        className="mr-2" 
                      />
                      <span className="group-hover:text-primary transition-colors">Sign in with Google</span>
                    </>
                  )}
                  
                  {/* Hover effect overlay */}
                  <span className="absolute inset-0 bg-gradient-to-tr from-primary/0 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
      
      <motion.div 
        className="relative hidden w-0 flex-1 lg:block"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="absolute inset-0 h-full w-full bg-gradient-to-br from-primary/90 to-primary/40 flex items-center justify-center overflow-hidden">
          <div className="relative w-full max-w-md p-8 text-white z-10">
            <motion.h2 
              className="text-4xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Welcome to GovCon Data
            </motion.h2>
            <motion.p 
              className="text-xl mb-8 text-white/90"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              Access critical insights and analytics for government contracting opportunities.
            </motion.p>
            
            {/* Animated background elements */}
            <div className="absolute inset-0 opacity-10">
              <motion.div
                className="absolute top-10 left-10 w-20 h-20 rounded-full bg-white"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.div
                className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-white"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.2, 0.5, 0.2],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
} 